// pages/Promotions.js - Refactored with new architecture, pagination and lazy loading
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Gift } from 'lucide-react';
import SinglePromotionPopup from '../components/promotion/SinglePromotionPopup';
import CategorySlider from '../components/promotion/CategorySlider';
import SearchBar from '../components/promotion/SearchBar';
import LoadingProgressBar from '../components/LoadingProgressBar';
import usePromotions from '../hooks/usePromotions';
import usePromotionFilters from '../hooks/usePromotionFilters';
import useInfiniteScroll from '../hooks/useInfiniteScroll';

function PromotionsPage() {
  // UI State
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [showSearch, setShowSearch] = useState(false);

  // Custom hooks for data management
  const {
    promotions,
    allPromotions,
    categories,
    loading,
    refreshing,
    loadingMore,
    loadingProgress,
    error,
    lastUpdated,
    hasMore,
    refreshPromotions,
    loadMore,
    resetToFirstPage
  } = usePromotions(50); // Load more promotions initially to show more content

  const {
    filters,
    filteredPromotions,
    updateCategory,
    updateSearchQuery,
    hasActiveFilters
  } = usePromotionFilters(allPromotions);

  // Infinite scroll for pagination
  const { isFetching, loadingRef } = useInfiniteScroll({
    hasMore: hasMore && !hasActiveFilters, // Only infinite scroll when no filters
    loadMore,
    threshold: 300,
    enabled: !loading && !error
  });


  // Handle search toggle
  const handleToggleSearch = () => {
    setShowSearch(!showSearch);
  };

  // Handle search input changes
  const handleSearchChange = (query) => {
    updateSearchQuery(query);
  };

  // Handle search clear
  const handleSearchClear = () => {
    updateSearchQuery('');
    setShowSearch(false);
  };

  // Handle category changes
  const handleCategoryChange = (category) => {
    updateCategory(category);
  };

  // Reset pagination when filters change
  useEffect(() => {
    if (hasActiveFilters) {
      resetToFirstPage(filteredPromotions);
    }
  }, [filteredPromotions, hasActiveFilters, resetToFirstPage]);

  // Determine which promotions to display
  const displayPromotions = hasActiveFilters ? filteredPromotions : promotions;

  // Log performance report after initial load
  useEffect(() => {
    if (!loading && promotions.length > 0) {
      // Delay to allow images to start loading
      setTimeout(() => {
        import('../utils/PromotionPerformanceMonitor').then(({ default: monitor }) => {
          monitor.logPerformanceReport();
        });
      }, 2000);
    }
  }, [loading, promotions.length]);

  // Preload critical images
  useEffect(() => {
    const preloadLinks = [];

    if (displayPromotions.length > 0) {
      const criticalImages = displayPromotions.slice(0, 3);
      criticalImages.forEach(promo => {
        if (promo.image) {
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.href = promo.image;
          document.head.appendChild(link);
          preloadLinks.push(link);
        }
      });
    }

    return () => {
      preloadLinks.forEach(link => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      });
    };
  }, [displayPromotions]);

  // Handle promotion selection for popup
  const handlePromotionClick = (promotion) => {
    setSelectedPromotion(promotion);
    document.body.style.overflow = 'hidden';
  };

  // Close promotion popup
  const handleClosePopup = () => {
    setSelectedPromotion(null);
    document.body.style.overflow = 'auto';
  };

  // Handle click outside popup to close it
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClosePopup();
    }
  };

  // Calculate responsive columns for masonry grid (1-5 columns)
  // Optimized for MacBook 13-inch and various screen sizes
  const [columns, setColumns] = useState(() => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width >= 1800) return 5;      // 5 columns for ultrawide monitors
      if (width >= 1500) return 4;      // 4 columns for large desktop monitors
      if (width >= 1024) return 3;      // 3 columns for MacBook 13-inch, laptops, medium desktop
      if (width >= 640) return 2;       // 2 columns for tablets, small laptops
      return 1;                         // 1 column for mobile
    }
    return 1;
  });

  useEffect(() => {
    function handleResize() {
      const width = window.innerWidth;
      if (width >= 1800) {
        setColumns(5); // 5 columns for ultrawide monitors (1800px+)
      } else if (width >= 1500) {
        setColumns(4); // 4 columns for large desktop monitors (1500px+)
      } else if (width >= 1024) {
        setColumns(3); // 3 columns for MacBook 13-inch, laptops, medium desktop (1024px+)
      } else if (width >= 640) {
        setColumns(2); // 2 columns for tablets, small laptops (640px+)
      } else {
        setColumns(1); // 1 column for mobile (<640px)
      }
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Create masonry columns like original
  const promotionColumns = [];
  for (let i = 0; i < columns; i++) {
    promotionColumns.push([]);
  }
  
  // Distribute promotions across columns for masonry grid
  displayPromotions.forEach((promo, index) => {
    const columnIndex = index % columns;
    promotionColumns[columnIndex].push({ ...promo, globalIndex: index });
  });

  if (loading) {
    return (
      <div className="max-w-full mx-auto pb-8">
        {/* Category Slider (loading state) */}
        <div className="overflow-x-auto w-full bg-gray-50">
          <div className="flex space-x-2 min-w-max px-4 py-3">
            <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
            <div className="w-16 h-8 bg-gray-200 rounded-full animate-pulse"></div>
            <div className="w-20 h-8 bg-gray-200 rounded-full animate-pulse"></div>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="px-4 mt-6">
          <LoadingProgressBar 
            isLoading={loading}
            progress={loadingProgress}
            showPercentage={true}
            color="blue"
          />
        </div>
        
        {/* Loading Grid Skeleton */}
        <div className="px-4 mt-6">
          <div className="flex flex-row -mx-2">
            {Array.from({ length: columns }, (_, columnIndex) => (
              <div
                key={columnIndex}
                className="px-2"
                style={{ width: `${100 / columns}%` }}
              >
                {Array.from({ length: 3 }, (_, itemIndex) => (
                  <div key={itemIndex} className="mb-4 bg-white rounded-lg overflow-hidden shadow-sm animate-pulse">
                    <div className="bg-gray-200 w-full h-48" />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-4 text-center py-12">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Promosi Casino Online by Category - Cek Bonus</title>
        <meta
          name="description"
          content="Jelajahi promosi casino online berdasarkan kategori: Welcome Bonus, Losing Rescue, Rebate, dan lainnya. Temukan bonus terbaik sesuai preferensi Anda di Indonesia."
        />
        <meta
          name="keywords"
          content="promosi casino kategori, welcome bonus casino, losing rescue bonus, rebate casino, referral bonus, casino online indonesia"
        />
        <link rel="canonical" href="https://cekbonus.com/bycategory" />

        {/* Open Graph */}
        <meta property="og:title" content="Promosi Casino Online by Category - Cek Bonus" />
        <meta property="og:description" content="Jelajahi promosi casino online berdasarkan kategori. Welcome Bonus, Losing Rescue, Rebate, dan kategori bonus lainnya." />
        <meta property="og:url" content="https://cekbonus.com/bycategory" />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:title" content="Promosi Casino Online by Category - Cek Bonus" />
        <meta name="twitter:description" content="Jelajahi promosi casino online berdasarkan kategori bonus yang tersedia." />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Promosi Casino Online by Category",
            "description": "Halaman promosi casino online berdasarkan kategori bonus",
            "url": "https://cekbonus.com/bycategory",
            "mainEntity": {
              "@type": "ItemList",
              "name": "Casino Promotion Categories",
              "description": "Daftar kategori promosi casino: Welcome Bonus, Losing Rescue, Rebate, dll"
            }
          })}
        </script>
      </Helmet>

      <div className="max-w-full mx-auto pb-8">
      {/* Search Bar - Only shown when showSearch is true */}
      {showSearch && (
        <div className="px-4 mt-3">
          <SearchBar
            value={filters.searchQuery}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
            autoFocus={true}
            className="animate-fadeIn"
          />
        </div>
      )}
      
      {/* Category Slider */}
      <CategorySlider
        categories={categories}
        selectedCategory={filters.category}
        onCategoryChange={handleCategoryChange}
        onToggleSearch={handleToggleSearch}
        showRefresh={false}
        stickyOffset={56}
      />
      
      {/* No Results Message */}
      {displayPromotions.length === 0 && (
        <div className="text-center py-8">
          <Gift className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-1">No promotions found</h3>
          <p className="text-gray-500">
            {hasActiveFilters 
              ? "Try adjusting your search or filters" 
              : "Check back later for new promotions"}
          </p>
        </div>
      )}
      
      {/* Responsive Masonry Grid */}
      <div className="px-4">
        <div className="flex flex-row -mx-2">
          {/* Create columns */}
          {promotionColumns.map((columnPromotions, columnIndex) => (
            <div
              key={columnIndex}
              className="px-2"
              style={{ width: `${100 / columns}%` }}
            >
              {/* Promotions in this column */}
              {columnPromotions.map((promotion, promoIndex) => (
                <div 
                  key={`${columnIndex}-${promoIndex}`} 
                  className="mb-4 bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handlePromotionClick(promotion)}
                >
                  {/* Full Promotion Image with Gradient Overlay for Title */}
                  <div className="relative">
                    {promotion.image ? (
                      <div className="relative">
                        <div className="w-full overflow-hidden">
                          <img
                            src={promotion.image}
                            alt={promotion.title || "Promotion"}
                            className="w-full h-auto object-cover"
                            loading={promotion.globalIndex < 6 ? "eager" : "lazy"}
                            decoding="async"
                          />
                        </div>
                        {/* Gradient overlay from bottom to top */}
                        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black to-transparent"></div>

                        {/* Title on gradient */}
                        {promotion.title && (
                          <h3 className="absolute bottom-3 left-3 right-3 text-white font-semibold text-sm md:text-base line-clamp-2">
                            {promotion.title}
                          </h3>
                        )}
                      </div>
                    ) : (
                      <div className="relative bg-blue-50 w-full min-h-[200px] flex items-center justify-center">
                        <Gift className="h-12 w-12 text-blue-300" />
                        {/* Gradient overlay */}
                        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black to-transparent"></div>

                        {/* Title on gradient */}
                        {promotion.title && (
                          <h3 className="absolute bottom-3 left-3 right-3 text-white font-semibold text-sm md:text-base line-clamp-2">
                            {promotion.title}
                          </h3>
                        )}
                      </div>
                    )}
                    
                    {/* Brand Logo Overlay - Top right with white border */}
                    <Link 
                      to={`/brand/${promotion.brandSlug}`} 
                      className="absolute top-2 right-2"
                      onClick={(e) => e.stopPropagation()} 
                    >
                      {promotion.brandLogo ? (
                        <img
                          src={promotion.brandLogo}
                          alt="Brand logo"
                          className="h-8 w-8 object-contain rounded-full bg-white shadow-sm border-2 border-white"
                          loading={promotion.globalIndex < 6 ? "eager" : "lazy"}
                          decoding="async"
                          width="32"
                          height="32"
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-500 font-bold shadow-sm border-2 border-white">
                          {promotion.brandName ? promotion.brandName.charAt(0).toUpperCase() : "B"}
                        </div>
                      )}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
        
        {/* Infinite Scroll Loading Indicator */}
        {!hasActiveFilters && hasMore && !loading && (
          <div ref={loadingRef} className="flex justify-center py-8">
            {(loadingMore || isFetching) && (
              <div className="flex items-center space-x-2 text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-600"></div>
                <span>Loading more promotions...</span>
              </div>
            )}
          </div>
        )}
        
        {/* Load More Button (fallback for infinite scroll) */}
        {!hasActiveFilters && hasMore && !loading && !loadingMore && !isFetching && (
          <div className="flex justify-center py-8">
            <button
              onClick={loadMore}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Load More Promotions
            </button>
          </div>
        )}
      </div>
      
      {/* Promotion Detail Popup */}
      {selectedPromotion && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4 animate-fadeIn"
          onClick={handleOverlayClick}
        >
          <SinglePromotionPopup 
            promotion={selectedPromotion} 
            onClose={handleClosePopup} 
          />
        </div>
      )}

      {/* Global Styles */}
      <style>{`
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-in-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
      </div>
    </>
  );
}

export default PromotionsPage;